import { UseEntityData } from "crqlar-react-common/dist/components/useEntity";
import { useTranslation } from "react-i18next";
import { Alert, Divider, MenuItem, Select, Stack, Switch } from "@mui/material";
import Typography from "@mui/material/Typography";
import Property from "crqlar-react-common/dist/components/Property";
import TextField from "@mui/material/TextField";
import OtaMappingRulesForm from "../../OtaMappingRulesForm";
import { ProtelConfig } from "crqlar-react-common/dist/models/integrations/protel/protelConfig.model";
import { ProcessingStrategy } from "crqlar-react-common/dist/models/integrations/baseIntegrationConfig.model";
import {isRequiredTextMissing} from "src/utils/requiredField.util";

interface Props {
  protelConfigData: UseEntityData<ProtelConfig>;
}


const ProtelConfigForm = ({ protelConfigData }: Props) => {
  const { t } = useTranslation();

  const protelConfig = protelConfigData.item;
  const nameMissing = isRequiredTextMissing(protelConfigData, protelConfig.name);
  const setProtelConfig = protelConfigData.setItem;

  const integrationInstructions = `
There are 2 authentication methods for the Protel integration:

1. Use HTTP Basic Authentication with the username and password provided above.

Authorization: Basic base64(${protelConfig.id}:${protelConfig.authorizationToken})

Or

2. Use POS XML Element:

<POS>
    <Source>
        <RequestorID ID="${protelConfig.id}" MessagePassword="${protelConfig.authorizationToken}" />
    </Source>
</POS>

    `.trim();

  //region Handlers
  const updateField = (field: keyof ProtelConfig, value: any) => {
    setProtelConfig({ ...protelConfig, [field]: value });
  };
  //endregion Handlers

  //region UI

  let integrationUI;
  if (protelConfig.id) {
    integrationUI =
    <>
                <Property
        title={t('Username')}
        tooltip={t('Generated username for authenticating Protel requests.')}>

                    <TextField
          id={'username'}
          size={'small'}
          value={protelConfig.id || ''}
          fullWidth
          disabled />

                </Property>

                <Property
        title={t('Password / Auth token')}
        tooltip={t('Generated token used as the password for Protel requests.')}>

                    <TextField
          id={'token'}
          size={'small'}
          value={protelConfig.authorizationToken || ''}
          fullWidth
          disabled />

                </Property>

                <Property
        title={t('Integration instructions')}
        tooltip={t('Endpoint and authentication details for sending data into this connector.')}>

                    <Stack gap={1}>
                        <Alert
            variant={'outlined'}
            severity={'info'}
            sx={{ py: 0, lineHeight: 1, alignItems: 'center' }}>

                            <Typography>
                                {t("Send")} <strong>{t("SOAP")}</strong> messages
                                to <strong>{process.env.REACT_APP_API_ENDPOINT}{t("/v3/integrations/protel/soap")}</strong>
                            </Typography>
                        </Alert>

                        <Alert
            variant={'outlined'}
            severity={'info'}
            sx={{ py: 0, lineHeight: 1, alignItems: 'center' }}>

                            <Typography sx={{
              whiteSpace: 'pre-wrap'
            }}>
                                {integrationInstructions}
                            </Typography>
                        </Alert>
                    </Stack>

                </Property>
            </>;

  } else {
    integrationUI =
    <Alert
      variant={'outlined'}
      severity={'info'}
      sx={{ py: 0, lineHeight: 1, alignItems: 'center' }}>

                {t('Please save the Protel configuration first to see the integration details')}
            </Alert>;

  }

  const credentialsSection =
  <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Credentials')}
            </Typography>

            <Stack gap={1}>
                <Property
        title={t('Name')}
        tooltip={t('Internal label for identifying this Protel integration configuration.')}>

                    <TextField
          id={'name'}
          size={'small'}
          value={protelConfig.name || ''}
          error={nameMissing}
          onChange={(e) => updateField('name', e.target.value)}
          fullWidth
          required />

                </Property>

                <Property
        title={t('Response endpoint')}
        tooltip={t('Protel endpoint where response SOAP messages should be sent.')}>

                    <TextField
          id={'soap-endpoint'}
          size={'small'}
          value={protelConfig.protelSoapEndpoint || ''}
          onChange={(e) => updateField('protelSoapEndpoint', e.target.value)}
          fullWidth
          required />

                </Property>

                <Property
        title={t('Protel token')}
        tooltip={t('Token used when Crqlar sends SOAP responses back to Protel.')}>

                    <TextField
          id={'soap-token'}
          size={'small'}
          value={protelConfig.protelSoapToken || ''}
          onChange={(e) => updateField('protelSoapToken', e.target.value)}
          fullWidth
          required
          type={'password'} />

                </Property>

                {integrationUI}
            </Stack>
        </Stack>;


  const otaMappingRules =
  <OtaMappingRulesForm value={protelConfig.otaMappingRules} onChange={(r) => updateField('otaMappingRules', r)} />;


  const configurationSection =
  <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Configuration')}
            </Typography>

            <Stack gap={1}>
                <Property
        title={t('Enabled')}
        tooltip={t('Turns the Protel integration on or off.')}>

                    <Switch
          id={'enabled'}
          checked={protelConfig.enabled}
          onChange={(e) => updateField('enabled', e.target.checked)} />

                </Property>

                <Property
        title={t('Processing strategy')}
        tooltip={t('Controls whether imported messages are processed immediately or queued.')}>

                    <Select
          value={protelConfig.processingStrategy || ProcessingStrategy.IMMEDIATE}
          onChange={(e) => updateField('processingStrategy', e.target.value)}
          fullWidth
          size={'small'}
          variant={'outlined'}>

                        {
          Object.values(ProcessingStrategy).map((type, index) =>
          <MenuItem
            key={index}
            value={type}
            id={`processing-strategy-${type}`}>

                                    {t(`Processing strategy ${type}`)}
                                </MenuItem>
          )
          }
                    </Select>
                </Property>

                <Property
        title={t('Property ID / Name (optional, multiproperty)')}
        tooltip={t('Optional property identifier assigned to all data imported through this connector.')}>

                    <Stack
          width={'100%'}
          gap={1}>

                        <Alert
            variant={'outlined'}
            severity={'info'}
            sx={{ py: 0, lineHeight: 1, alignItems: 'center' }}>

                            {t('Put property id or name here. If set, all incoming data via this connector (profiles, reservations...) will have this property id. You will be able to filter data by this property id in the future')}
                        </Alert>

                        <Alert
            variant={'outlined'}
            severity={'error'}
            sx={{ py: 0, lineHeight: 1, alignItems: 'center' }}>

                            {t('Do not change this value if you are not sure what you are doing! It may cause data inconsistency. If you want to change it, please first delete all data from this connector')}
                        </Alert>

                        <TextField
            id={'name'}
            size={'small'}
            value={protelConfig.propertyId || ''}
            onChange={(e) => updateField('propertyId', e.target.value)}
            fullWidth
            required />

                    </Stack>

                </Property>

                {otaMappingRules}
            </Stack>
        </Stack>;


  //endregion UI

  return (
    <Stack
      sx={{
        width: '100%',
        gap: 6
      }}>

            {credentialsSection}
            <Divider />
            {configurationSection}
        </Stack>);

};

export default ProtelConfigForm;
